import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type {
  TenantProvisioningView,
  TenantResourceTargetsView,
} from "@shared/types/tenantResources";
import { apiRequest, ApiClientError } from "@/lib/api";
import { TENANTS_PATH } from "@/hooks/use-tenants";
import { PROVISIONING_POLL_INTERVAL_MS } from "@/hooks/use-instances-admin";

/**
 * A tenant's own Azure resources (ADR 0010, design §7.2 and §7.5): the stepper's job, its retry and Apply now,
 * and the registered placement targets with the default for new tenants.
 *
 * Nothing here runs unless the Tenants page found a `resources` view on a row: a shared
 * deployment (the default) never asks for any of these.
 */

const PLATFORM_PATH = "/api/v2/platform";

export const TENANT_RESOURCE_TARGETS_PATH = `${PLATFORM_PATH}/tenant-resources/targets`;
const DEFAULT_TARGET_PATH = `${PLATFORM_PATH}/tenant-resources/default-target`;

export function tenantProvisioningPath(tenantId: string): string {
  return `${TENANTS_PATH}/${tenantId}/provisioning`;
}

/** Sentinel for a disabled query, as `PROVISIONING_DISABLED_KEY` is for an instance's. */
const TENANT_PROVISIONING_DISABLED_KEY = "tenant-provisioning-disabled";

/** The tenant list and its views, not the provisioning reads (those poll on their own). */
function isTenantListQuery(queryKey: readonly unknown[]): boolean {
  const path = String(queryKey[0]);
  return path.startsWith(TENANTS_PATH) && !path.endsWith("/provisioning");
}

/** A 404 (no job) or a 403 (not this caller's to read) will answer the same next time. */
function isRefusedForGood(error: unknown): boolean {
  return error instanceof ApiClientError && (error.status === 404 || error.status === 403);
}

/**
 * Poll while a job is queued or running or a step is running or pending; stop once everything has
 * settled (or the route refuses for good). A function of the data, so a retry that starts a new
 * job restarts the poll without a flag to flip.
 */
export function tenantProvisioningInterval(
  view: TenantProvisioningView | undefined,
  error?: unknown,
): number | false {
  if (isRefusedForGood(error)) return false;
  const active = (view?.jobs ?? []).some(
    (job) =>
      job.status === "queued" ||
      job.status === "running" ||
      job.steps.some((step) => step.status === "running" || step.status === "pending"),
  );
  return active ? PROVISIONING_POLL_INTERVAL_MS : false;
}

export function useTenantProvisioning(tenantId: string | null) {
  const queryClient = useQueryClient();
  const query = useQuery<TenantProvisioningView>({
    queryKey: [tenantId ? tenantProvisioningPath(tenantId) : TENANT_PROVISIONING_DISABLED_KEY],
    enabled: Boolean(tenantId),
    refetchInterval: (query) => tenantProvisioningInterval(query.state.data, query.state.error),
    retry: (failureCount, error) => !isRefusedForGood(error) && failureCount < 2,
    staleTime: 0,
  });
  // The Resources chip reads the tenant list: read it again once, when the jobs settle.
  const active = tenantProvisioningInterval(query.data) !== false;
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active) {
      void queryClient.invalidateQueries({
        predicate: (entry) => isTenantListQuery(entry.queryKey),
      });
    }
    wasActive.current = active;
  }, [active, queryClient]);
  return query;
}

/**
 * `POST /platform/tenants/:id/resources/retry` (requeue the newest failed job) or `.../apply`
 * ("Apply now", F31: a new apply job for an active or failed tenant) answer 202 when they queued a
 * job and 409 while one is already queued or running. Either way the stepper and the row are read
 * again.
 */
function useTenantResourcesJob(verb: "retry" | "apply") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) =>
      apiRequest<unknown>(`${TENANTS_PATH}/${tenantId}/resources/${verb}`, { method: "POST" }),
    onSettled: (_data, _error, tenantId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [tenantProvisioningPath(tenantId)] }),
        queryClient.invalidateQueries({
          predicate: (query) => isTenantListQuery(query.queryKey),
        }),
      ]),
  });
}

export function useRetryTenantResources() {
  return useTenantResourcesJob("retry");
}

export function useApplyTenantResources() {
  return useTenantResourcesJob("apply");
}

/** The registered targets and the default; read only for a PlatformAdmin of a per-tenant deployment. */
export function useTenantResourceTargets(enabled: boolean) {
  return useQuery<TenantResourceTargetsView>({
    queryKey: [TENANT_RESOURCE_TARGETS_PATH],
    enabled,
    retry: false,
  });
}

/** `PUT /platform/tenant-resources/default-target`: a registered id only; existing tenants never move. */
export function useSetDefaultTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetId: string) =>
      apiRequest<unknown>(DEFAULT_TARGET_PATH, { method: "PUT", body: { targetId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [TENANT_RESOURCE_TARGETS_PATH] }),
  });
}
